/**
 * OrderLinkedRecordsStrip
 * Operational flow: Lead → Quote → Payment → Booking → Ops
 * Shows state, owner, and action required for each step
 */

import React, { useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Button,
  Divider,
  Stack,
} from "@mui/material";
import {
  Description,
  RequestQuote,
  Payments,
  Event,
  LocalShipping,
  Launch,
} from "@mui/icons-material";

const upper = (v) => String(v || "").replaceAll("_", " ").toUpperCase();

const formatMoney = (value) => {
  const num = Number(value || 0);
  return `R${num.toLocaleString("en-ZA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const getPaymentState = (invoicesRollup) => {
  const balance = Number(invoicesRollup?.balance || 0);
  if (Number.isFinite(balance) && balance > 0.01) {
    return {
      label: `Outstanding ${formatMoney(balance)}`,
      color: "warning",
      tone: "action",
    };
  }
  if ((invoicesRollup?.count || 0) > 0) {
    return { label: "Clear", color: "success", tone: "settled" };
  }
  return { label: "No invoices yet", color: "default", tone: "action" };
};

export default function OrderLinkedRecordsStrip({
  order,
  quote,
  lead,
  invoicesRollup,
  onOpenLead,
  onOpenQuote,
  onOpenInvoices,
  onOpenBooking,
  onOpenOperations,
  onOpenLedger, // kept for compatibility (not shown in flow by default)
}) {
  const jobId = order?.jobId || order?.id;

  const steps = useMemo(() => {
    const payment = getPaymentState(invoicesRollup);
    const hasLead = Boolean(lead?.id || lead?._id || quote?.leadId);
    const hasQuote = Boolean(quote?.id || quote?._id || order?.quoteId);
    const hasBooking = Boolean(order?.booking?.moveDate || order?.booking?.status);

    const leadState = lead?.tracking?.lifecycleStage
      ? upper(lead.tracking.lifecycleStage)
      : hasLead
        ? "LINKED"
        : "NOT LINKED";

    const quoteState = quote?.status
      ? upper(quote.status)
      : hasQuote
        ? "LINKED"
        : "NOT LINKED";

    const bookingState = hasBooking
      ? upper(order?.booking?.status || "scheduled")
      : "NOT SCHEDULED";

    const opsState = upper(order?.status || "unknown");

    return [
      {
        key: "lead",
        title: "Lead",
        icon: <Description fontSize="small" />,
        stateChip: { label: leadState, color: hasLead ? "info" : "default" },
        action: {
          label: hasLead ? "Open" : "Find",
          onClick: onOpenLead,
          disabled: !hasLead,
        },
        helper: lead?.id || lead?._id || quote?.leadId ? `ID: ${lead?.id || lead?._id || quote?.leadId}` : "No lead linked",
      },
      {
        key: "quote",
        title: "Quote",
        icon: <RequestQuote fontSize="small" />,
        stateChip: {
          label: quoteState,
          color:
            quote?.status === "deposit_verified" || quote?.status === "ordered"
              ? "success"
              : quote?.status
                ? "info"
                : hasQuote
                  ? "info"
                  : "default",
        },
        action: {
          label: hasQuote ? "Open" : "Link",
          onClick: onOpenQuote,
          disabled: !hasQuote,
        },
        helper: quote?.id || quote?._id || order?.quoteId ? `ID: ${quote?.id || quote?._id || order?.quoteId}` : "No quote linked",
      },
      {
        key: "payment",
        title: "Payment",
        icon: <Payments fontSize="small" />,
        stateChip: { label: payment.label, color: payment.color },
        action: {
          label: "View invoices",
          onClick: onOpenInvoices,
          disabled: !jobId,
        },
        helper: `${invoicesRollup?.count || 0} invoice${(invoicesRollup?.count || 0) === 1 ? "" : "s"}`,
      },
      {
        key: "booking",
        title: "Booking",
        icon: <Event fontSize="small" />,
        stateChip: { label: bookingState, color: hasBooking ? "info" : "warning" },
        action: {
          label: hasBooking ? "Open" : "Schedule",
          onClick: onOpenBooking,
          disabled: !hasBooking,
        },
        helper: order?.booking?.moveDate ? `Move date set` : `No move date`,
      },
      {
        key: "ops",
        title: "Ops",
        icon: <LocalShipping fontSize="small" />,
        stateChip: {
          label: opsState,
          color:
            order?.status === "arrived_at_storage" || order?.status === "completed"
              ? "success"
              : order?.status === "cancelled"
                ? "error"
                : "info",
        },
        action: {
          label: "Open ops",
          onClick: onOpenOperations,
          disabled: false,
        },
        helper: `Order: ${order?.id || "N/A"}`,
      },
    ];
  }, [order, quote, lead, invoicesRollup, onOpenLead, onOpenQuote, onOpenInvoices, onOpenBooking, onOpenOperations]);

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2} flexWrap="wrap">
          <Box>
            <Typography variant="h6">Operational Flow</Typography>
            <Typography variant="body2" color="text.secondary">
              Job: {jobId || "N/A"}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {Number(invoicesRollup?.balance || 0) > 0.01 ? (
              <Chip
                color="warning"
                variant="outlined"
                label={`Outstanding: ${formatMoney(invoicesRollup.balance)}`}
              />
            ) : (
              <Chip
                color="success"
                variant="outlined"
                label="Payments clear"
              />
            )}
          </Stack>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Stepper alternativeLabel sx={{ mb: 2 }}>
          {steps.map((s) => (
            <Step key={s.key} active>
              <StepLabel icon={s.icon}>{s.title}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "repeat(5, 1fr)" }} gap={2}>
          {steps.map((s) => (
            <Card key={s.key} variant="outlined" sx={{ height: "100%" }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={1}>
                  <Typography variant="subtitle2">{s.title}</Typography>
                  <Chip size="small" label={s.stateChip.label} color={s.stateChip.color} />
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {s.helper}
                </Typography>

                <Button
                  size="small"
                  variant="outlined"
                  endIcon={<Launch />}
                  onClick={s.action.onClick}
                  disabled={s.action.disabled}
                  sx={{ textTransform: "none" }}
                >
                  {s.action.label}
                </Button>
              </CardContent>
            </Card>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}
